import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  validateHandleFormat, 
  normalizeHandle,
  HANDLE_MIN_LENGTH,
  HANDLE_MAX_LENGTH,
  type HandleValidationResult 
} from '@/lib/handle';
import { debug } from '@/utils/debug';

export type HandleStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'taken';

export interface UseHandleValidationReturn {
  status: HandleStatus;
  message: string;
  validate: (handle: string, currentHandle?: string) => void;
  reset: () => void;
}

/**
 * React hook for handle validation with debounced RPC availability check
 * 
 * Usage:
 * ```tsx
 * const { status, message, validate } = useHandleValidation();
 * 
 * <Input 
 *   value={handle}
 *   onChange={(e) => {
 *     setHandle(e.target.value);
 *     validate(e.target.value, existingHandle);
 *   }}
 * />
 * {status === 'invalid' && <span className="text-destructive">{message}</span>}
 * {status === 'valid' && <span className="text-green-500">Available!</span>}
 * ```
 */
export function useHandleValidation(): UseHandleValidationReturn {
  const [status, setStatus] = useState<HandleStatus>('idle');
  const [message, setMessage] = useState('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckedRef = useRef<string>('');
  
  const reset = useCallback(() => {
    setStatus('idle');
    setMessage('');
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);
  
  const validate = useCallback((handle: string, currentHandle?: string) => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    const normalized = normalizeHandle(handle);
    
    // Empty check
    if (!normalized) {
      setStatus('invalid');
      setMessage('Handle is required');
      return;
    }
    
    // Too short - give immediate feedback but don't check availability yet
    if (normalized.length < HANDLE_MIN_LENGTH) {
      setStatus('invalid');
      setMessage(`At least ${HANDLE_MIN_LENGTH} characters`);
      return;
    }
    
    // Too long
    if (normalized.length > HANDLE_MAX_LENGTH) {
      setStatus('invalid');
      setMessage(`Maximum ${HANDLE_MAX_LENGTH} characters`);
      return;
    }
    
    // Same as current handle (no change needed)
    if (currentHandle && normalized === normalizeHandle(currentHandle)) {
      setStatus('valid');
      setMessage('Current handle');
      return;
    }
    
    // Format validation (immediate)
    const formatResult: HandleValidationResult = validateHandleFormat(handle);
    if (!formatResult.valid) {
      setStatus('invalid');
      setMessage(formatResult.error || 'Invalid format');
      return;
    }
    
    // Skip RPC if we just checked this handle
    if (normalized === lastCheckedRef.current) {
      return;
    }
    
    // Debounced availability check with timeout
    setStatus('checking');
    setMessage('Checking availability...');
    
    const VALIDATION_TIMEOUT = 5000; // 5 seconds - fail-open if exceeded
    
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), VALIDATION_TIMEOUT);
      
      try {
        debug.log('[useHandleValidation] Checking availability for:', normalized);
        lastCheckedRef.current = normalized;
        
        const { data, error } = await supabase.rpc('check_handle_available', {
          p_handle: normalized
        });
        
        clearTimeout(timeoutId);
        
        if (error) {
          debug.error('[useHandleValidation] RPC error:', error);
          // FAIL-OPEN: Allow user to proceed with current handle
          setStatus('valid');
          setMessage('Couldn\'t verify - you can proceed');
          return;
        }
        
        if (data === true) {
          setStatus('valid');
          setMessage('Available!');
          debug.log('[useHandleValidation] Handle available:', normalized);
        } else {
          setStatus('taken');
          setMessage('This handle is already taken');
          debug.log('[useHandleValidation] Handle taken:', normalized);
        }
      } catch (err: any) {
        clearTimeout(timeoutId);
        
        if (err.name === 'AbortError') {
          // Timeout - fail open to prevent blocking onboarding
          debug.warn('[useHandleValidation] Timeout - failing open');
          setStatus('valid');
          setMessage('Verification timed out - you can proceed');
          return;
        }
        
        debug.error('[useHandleValidation] Unexpected error:', err);
        // FAIL-OPEN: Allow user to proceed on error
        setStatus('valid');
        setMessage('Couldn\'t verify - you can proceed');
      }
    }, 400); // 400ms debounce
  }, []);
  
  return { status, message, validate, reset };
}

// Re-export types and constants for convenience
export { HANDLE_MIN_LENGTH, HANDLE_MAX_LENGTH };
